import { useState } from "react";
import { ShieldCheck, PenLine, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/app/Common";
import { cn } from "@/lib/utils";

const CAT = {
  vague: ["Vague", "bg-caution-tint text-caution"],
  unrealistic: ["Unrealistic", "bg-info-tint text-info"],
  exclusionary: ["Exclusionary wording", "bg-risk-tint text-risk"],
  other: ["Other", "bg-subtle text-ink-secondary"],
};

export function QualityPanel({ quality, jdText, onApply }) {
  const [applied, setApplied] = useState({});
  const issues = quality?.issues || [];
  const score = quality?.score;
  const tone = score == null ? "text-ink-muted" : score >= 75 ? "text-success" : score >= 50 ? "text-caution" : "text-risk";

  const apply = (issue, i) => {
    if (!issue.excerpt || !jdText.includes(issue.excerpt)) {
      toast.error("That wording is no longer in the description");
      return;
    }
    onApply(jdText.replace(issue.excerpt, issue.suggestion));
    setApplied((a) => ({ ...a, [i]: true }));
    toast.success("Rewrite applied to the job description");
  };

  return (
    <section className="panel space-y-4 p-5" data-testid="jd-quality-panel">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <ShieldCheck size={18} className="text-teal" /> JD quality check
          <InfoTip text="Flags vague requirements, unrealistic combinations and gendered or exclusionary wording, with suggested rewrites." />
        </h3>
        <span className={cn("font-display text-2xl font-semibold", tone)} data-testid="jd-quality-score">{score ?? "—"}<span className="text-sm text-ink-muted">/100</span></span>
      </div>
      {!issues.length && <p className="text-sm text-ink-secondary">No issues found. Nice and clear.</p>}
      <ul className="space-y-3">
        {issues.map((it, i) => (
          <li key={i} className="rounded-[6px] border border-line p-3 space-y-2" data-testid={`jd-quality-issue-${i}`}>
            <span className={cn("inline-block rounded-full px-2 py-0.5 text-xs font-medium", CAT[it.category]?.[1])}>{CAT[it.category]?.[0]}</span>
            {it.excerpt && <p className="text-sm text-ink"><span className="bg-caution-tint px-1">"{it.excerpt}"</span></p>}
            <p className="text-sm text-ink-secondary">{it.issue}</p>
            {it.suggestion && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-subtle px-3 py-2">
                <p className="text-sm text-ink">Try: <span className="font-medium">{it.suggestion}</span></p>
                {it.excerpt && (applied[i] ? (
                  <span className="inline-flex items-center gap-1 text-xs text-success"><Check size={14} /> Applied</span>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 text-brand" onClick={() => apply(it, i)} data-testid={`jd-quality-apply-${i}`}>
                    <PenLine size={14} /> Apply
                  </Button>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
